import * as React from 'react';
import * as css from './TeamMembersList.scss';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import SettingsSmall from 'wix-ui-icons-common/SettingsSmall';
import AddSmall from 'wix-ui-icons-common/AddSmall';
import { TeamMemberTableRowData, MembersTableState } from '../types';
import { generateRolesFiltersOption } from './TeamMembersListComponents';
import { withExperiments, InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { RolesData } from '../../types/roles';
import {
  reportCreateCustomRoleClick,
  reportManageRolesClick,
  reportTeammatesPageFilterByRole
} from '../../redux/actions/bi.actions';
import { useDispatch } from 'react-redux';

import { TableToolbar, MultiSelectCheckbox, Text, Divider, TextButton, Box } from 'wix-style-react';

interface IProps extends InjectedTranslateProps, InjectedExperimentsProps {
  data: TeamMemberTableRowData[];
  membersTable: MembersTableState;
  isAllowedToManageRoles: boolean;
  roleIds: string[];
  roles: RolesData;

  onRoleFilterAdded(filter: string, filters: string[]): void;

  onRoleFilterRemoved(filter: string, filters: string[]): void;
}

const TeamMembersListRolesFilter: React.FC<IProps> = ({ t, data, membersTable, roleIds, onRoleFilterAdded, onRoleFilterRemoved, roles, experiments , isAllowedToManageRoles }) => {

  const dispatch = useDispatch();

  const history = useHistory();

  const onManageRolesClick = useCallback(() => {
    dispatch(reportManageRolesClick('/rolesFilter'));
    history.push('/roles', { origin: '/rolesFilter' });
  }, [dispatch, history]);

  const onCreateNewRoleClick = useCallback(() => {
    dispatch(reportCreateCustomRoleClick('/rolesFilter'));
    history.push('/role/new', { biOrigin: '/rolesFilter' });
  }, [dispatch, history]);

  const selectedRoleFilters = membersTable.filters.roles.length === 0 ?
    [...roleIds, 'all'] : roleIds.filter(roleId => !membersTable.filters.roles.includes(roleId));
  const mapRoleIdsCount: { [key: string]: number } = {};
  roleIds.forEach((roleId: string) =>
    mapRoleIdsCount[roleId] = data.filter(member => member.roles.some(role => role === roleId)).length);
  const rolesFilterOptions: { id: string, value: any, inputTitle: string, disabled?: boolean }[] =
  roleIds
    .map(generateRolesFiltersOption(mapRoleIdsCount, roles))
    .filter(roleFilter => roleFilter.roleTitle)
    .sort((a, b) => mapRoleIdsCount[b.id] - mapRoleIdsCount[a.id]);
  rolesFilterOptions
  .unshift({
    id: 'all',
    value: (<div className={css.roleFilterRow}><span title={t('teamDetails.actions.rolesFilter.allOption')}>{t('teamDetails.actions.rolesFilter.allOption')}</span><Text light secondary className={css.badge}>{data.length}</Text></div>),
    inputTitle: t('teamDetails.actions.rolesFilter.allOption'),
  });

  const multiSelectValueParser = useCallback((option: { id: string, value: any, inputTitle: string, roleTitle?: string }) => {
    if (selectedRoleFilters.includes('all')) {
      return '';
    }
    return option.roleTitle;
  }, [selectedRoleFilters]);

  const onCloseAction = useCallback(() => {
    dispatch(reportTeammatesPageFilterByRole(rolesFilterOptions.filter(rolesFilterOption => rolesFilterOption.id !== 'all' && selectedRoleFilters.includes(rolesFilterOption.id)).length));
  }, [rolesFilterOptions, selectedRoleFilters, dispatch]);

  const delimiter = selectedRoleFilters.includes('all') ? '' : ', ';
  const experimentCustomRoles = experiments.enabled('specs.wos.CustomRoles');
  const rolesFilterFixedFooter = experimentCustomRoles && isAllowedToManageRoles ? (
  <div className={css.rolesFilter}>
    <div className={css.divider}><Divider/></div>
    <div><TextButton prefixIcon={<AddSmall />} onClick={onCreateNewRoleClick}>{t('teamDetails.actions.rolesFilter.create')}</TextButton></div>
    <div><TextButton prefixIcon={<SettingsSmall />} onClick={onManageRolesClick}>{t('teamDetails.actions.rolesFilter.manage')}</TextButton></div>
  </div>) : null;
  return (

    <Box>
      <TableToolbar.Label>
      {t('teamDetails.actions.rolesFilter.title')}
      </TableToolbar.Label>
      <Box marginLeft="14px">
        <MultiSelectCheckbox
          autoSelect={false}
          onSelect={(option: string) => onRoleFilterAdded(option, roleIds)}
          onDeselect={(option: string) => onRoleFilterRemoved(option, roleIds)}
          valueParser={multiSelectValueParser}
          options={rolesFilterOptions}
          selectedOptions={selectedRoleFilters}
          placeholder={t('teamDetails.actions.rolesFilter.placeholder')}
          data-hook="account-team-table-roles-filter"
          textOverflow="ellipsis"
          markedOption
          roundInput
          delimiter={delimiter}
          className={css.rolesFilterInput}
          fixedFooter={rolesFilterFixedFooter}
          onClose={onCloseAction}
          dropdownWidth="250px"
          popoverProps={{ placement: 'bottom-start' }}
        />
      </Box>
    </Box>
  );
};

export default withExperiments(translate()(TeamMembersListRolesFilter));
